import React, { useState } from "react";
import AsyncSelect from "react-select/async";
import { shippingReportsApi } from "../../../services/shippingReportsApi";
import { ReportShipment } from "../../../types/shippingReports";

type Props = {
  reportId: string;
  shipments: ReportShipment[];
  disabled: boolean;
  onAttach: (ids: number[]) => Promise<boolean>;
  onRemove: (id: number) => Promise<boolean>;
  onFailure: (result: any, message: string) => void;
};
type Option = { value: number; label: string };
export default function ShippingReportShipments({ reportId, shipments, disabled, onAttach, onRemove, onFailure }: Props) {
  const [selected, setSelected] = useState<readonly Option[]>([]);
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0);
  const loadOptions = async (search: string) => {
    const result = await shippingReportsApi.availableShipments(reportId, search);
    if (!result.success) { onFailure(result, "Could not load available shipments"); return []; }
    return result.data.map((s: ReportShipment) => ({ value: s.id, label: `Shipment #${s.id} · PO ${s.po_number || "N/A"} · ${s.customer_name || "N/A"}` }));
  };
  const attach = async () => {
    setBusy(true);
    try { if (await onAttach(selected.map(s => s.value))) { setSelected([]); setRevision(r => r + 1); } }
    finally { setBusy(false); }
  };
  const remove = async (id: number) => {
    setBusy(true);
    try { if (await onRemove(id)) setRevision(r => r + 1); }
    finally { setBusy(false); }
  };
  return <section className="mb-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
    <h2 className="text-base font-semibold text-slate-900">Shipments</h2>
    <p className="mt-1 text-sm text-slate-500">Search by shipment number, PO or customer. Shipments assigned to another report are unavailable.</p>
    {!disabled && <div className="mt-4 flex flex-col gap-3 sm:flex-row">
      <div className="min-w-0 flex-1"><AsyncSelect<Option, true> key={revision} isMulti defaultOptions loadOptions={loadOptions} value={selected} onChange={setSelected} isDisabled={busy} inputId="report-shipments" aria-label="Select shipments" placeholder="Select shipments..." /></div>
      <button type="button" disabled={busy || !selected.length} onClick={attach} className="rounded-md bg-main-purple px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{busy ? "Saving..." : "Add Shipments"}</button>
    </div>}
    <div className="mt-4 grid gap-3 sm:grid-cols-2">
      {shipments.map(s => <article key={s.id} className="rounded-lg border border-slate-200 p-4">
        <div className="flex justify-between gap-3"><a href={`/shippings-v2/${s.id}`} className="font-semibold text-indigo-700">Shipment #{s.id}</a>
          {!disabled && <button type="button" disabled={busy || shipments.length <= 1} onClick={() => remove(s.id)} className="text-sm text-rose-600 disabled:opacity-30" aria-label={`Remove shipment ${s.id}`}>Remove</button>}
        </div>
        <p className="mt-2 text-sm text-slate-700">PO #: {s.po_number || "N/A"}</p>
        <p className="text-sm text-slate-700">Customer: {s.customer_name || "N/A"}</p>
      </article>)}
    </div>
  </section>;
}
